import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { coursesApi, type Marathon } from '@/api/courses';
import { MNote } from '@/components/mobile/MobileKit';
import { Button } from '@/components/ui/Button';
import { guestCourses } from '@/lib/guestMode';
import { titleInitial } from '@/lib/marathonCards';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';
import { GuestWordOfTheDay } from '@/pages/public/GuestWordOfTheDay';

import { GuestShell } from './GuestShell';

/**
 * Гостевая главная приложения (решение владельца 14.09.2026, договор guest-mode.md).
 *
 * Слово дня и курсы — только публичные ручки: закрытая в приложении отвечает гостю тихим выходом
 * без перехода, и экран остался бы с ошибками. «Начать» на курсе — регистрация, а после неё сразу
 * этот курс.
 */
export function GuestHomePage() {
  const { t } = useTranslation();
  const catalog = useQuery({
    queryKey: ['public', 'guest-courses'],
    queryFn: () => coursesApi.listPublishedMarathons({ limit: 50 }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const courses = guestCourses(catalog.data?.items);

  return (
    <GuestShell>
      <section>
        <h1 className="font-display text-[22px] font-bold tracking-display text-on-surface">{t('guest.title', { brand: IS_APP_BUILD ? APP_BRAND : 'onebilim' })}</h1>
        <p className="mt-1 text-sm text-on-surface-variant">{t('guest.subtitle')}</p>
      </section>

      <GuestWordOfTheDay />

      <section className="space-y-3">
        <h2 className="mx-0.5 font-display text-[15.5px] font-bold text-on-surface">{t('guest.coursesTitle')}</h2>
        {catalog.isSuccess && courses.length === 0 ? (
          <MNote icon="school" title={t('guest.coursesEmpty')} />
        ) : (
          courses.map((course) => <GuestCourseCard key={course.id} course={course} />)
        )}
      </section>

      <Link to="/register" className="block">
        <Button fullWidth>{t('guest.signUp')}</Button>
      </Link>
    </GuestShell>
  );
}

/** Карточка курса гостя: обложка, название, «Начать». Прогресса и замков нет — гостя в базе нет. */
function GuestCourseCard({ course }: { course: Marathon }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-[20px] bg-surface-container px-4 py-3">
      <span className="grid h-14 w-14 flex-none place-items-center overflow-hidden rounded-2xl bg-primary-container font-display text-lg font-bold text-on-primary-container">
        {course.cover_url ? (
          <img src={course.cover_url} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          titleInitial(course.title)
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-on-surface">{course.title}</span>
      <Link
        to="/register"
        state={{ next: `/app/lessons/marathon/${course.id}` }}
        className="flex-none rounded-full bg-primary px-4 py-[7px] text-[12px] font-bold text-on-primary"
      >
        {t('guest.start')}
      </Link>
    </div>
  );
}
